/**
 * ElencoQuestionariCompatto — l'elenco dei questionari sul telefono (sotto i 900 px).
 *
 * La tabella ha sei colonne e sul telefono costringeva a scorrere di lato per arrivare ai bottoni. Qui ogni
 * questionario è una riga a due piani dentro lo stesso riquadro: sopra il titolo e la descrizione, sotto lo stato,
 * le domande, i contatti e la data, e a destra le due azioni che si usano dal telefono (link e modifica). Toccare
 * la riga apre statistiche e iscritti, come sul computer. Stessi dati e stesse azioni della tabella: cambia solo
 * la disposizione.
 */

import React from 'react';
import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { EditOutlined, Link as LinkIcon } from '@mui/icons-material';
import { normalizzaQuestionario } from '@shared/questionari';
import { tokens } from '../../../theme';
import { SoftChip } from '../../SoftChip';
import { formatDate } from '../labels';
import type { PlatformQuestionnaire } from '../../../types/platformCrm';

interface Props {
  questionari: PlatformQuestionnaire[];
  onApri: (q: PlatformQuestionnaire) => void;
  onModifica: (q: PlatformQuestionnaire) => void;
  onLink: (q: PlatformQuestionnaire) => void;
}

export const ElencoQuestionariCompatto: React.FC<Props> = ({ questionari, onApri, onModifica, onLink }) => {
  if (questionari.length === 0) {
    return (
      <Typography
        sx={{
          p: 4, textAlign: 'center', fontFamily: tokens.font.display, fontSize: '1.05rem',
          fontWeight: 500, fontStyle: 'italic', color: tokens.color.mist,
        }}
      >
        Nessun questionario: crea il primo con "Nuovo questionario".
      </Typography>
    );
  }

  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {questionari.map((q) => {
        const domande = q.domande?.length ?? 0;
        return (
          <Box
            component="li"
            key={q.id}
            sx={{
              display: 'flex', alignItems: 'stretch',
              '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.color.lineSoft}` },
            }}
          >
            <ButtonBase
              onClick={() => onApri(q)}
              sx={{ flex: 1, minWidth: 0, display: 'block', textAlign: 'left', px: 2, py: 1.5 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}>
                {q.titolo}
              </Typography>
              {q.descrizione && (
                <Typography
                  variant="caption"
                  sx={{
                    color: tokens.color.mist, display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25,
                  }}
                >
                  {q.descrizione}
                </Typography>
              )}
              <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                {q.attivo
                  ? <SoftChip label="Attivo" tone="success" />
                  : <SoftChip label="Disattivo" tone="default" outlined />}
                <SoftChip label={`${domande} ${domande === 1 ? 'domanda' : 'domande'}`} tone="default" outlined />
                {normalizzaQuestionario(q).contatti
                  ? <SoftChip label="Contatti chiesti" tone="info" />
                  : <SoftChip label="Contatti non chiesti" tone="default" outlined />}
                <Typography variant="caption" sx={{ color: tokens.color.mist, fontFamily: tokens.font.mono }}>
                  {formatDate(q.created_at)}
                </Typography>
              </Stack>
            </ButtonBase>
            {/* ⚠️ Bersagli da 44 px: sul telefono un'icona da 32 accanto a un'altra si sbaglia col dito. */}
            <Stack justifyContent="center" sx={{ pr: 1, pl: 0.5 }}>
              <IconButton
                aria-label={`Link di invio di ${q.titolo}`}
                onClick={() => onLink(q)}
                sx={{ width: 44, height: 44, color: tokens.color.tierPro.main }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={`Modifica ${q.titolo}`}
                onClick={() => onModifica(q)}
                sx={{ width: 44, height: 44 }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};
